/*
 * Reconciling the head the build wrote with the head the app writes.
 *
 * THE SITUATION. A page served from dist/ arrives with a complete crawler head
 * already in it -- title, description, canonical, og:*, twitter:* -- written by
 * src/scripts/injectHeadTags.js so that facebookexternalhit, linkedinbot and
 * Googlebot see a real preview without running a line of JavaScript. Then the
 * app boots and @unhead/vue writes the same information again from the view.
 * Both sets live in one <head>.
 *
 * WHY THAT IS NOT SELF-CORRECTING. @unhead/vue 1.8 does adopt an existing head
 * element when its own tag hashes to the same identity (see @unhead/dom's
 * renderDOMHead: it seeds an element map from the head's children on first
 * render, keyed by @unhead/shared's tagDedupeKey), so in the normal case the
 * static tag is reused and updated in place rather than duplicated. That is a
 * dependency's internal hashing, not a contract we own: any tag it does not
 * adopt gets appended alongside the static one, and then the page has two
 * og:titles. Crawlers pick one by rules nobody controls, and a Playwright
 * strict-mode locator on meta[name="description"] throws when it matches twice,
 * which takes the e2e suite down with it. So this module makes the outcome
 * deterministic instead of inherited.
 *
 * THE CONTRACT, IN ONE SENTENCE. data-al-head="static" means "written by the
 * build and not yet claimed by the client head". Two rules keep that true:
 *
 *   1. When unhead renders into an element, that element is claimed -- the
 *      marker comes off (`dom:renderTag`).
 *   2. After a render, any still-marked tag that a live tag now duplicates is
 *      removed (`dom:rendered`). It can never be an element unhead adopted,
 *      because adoption unmarks it and appends nothing, so this sweep cannot
 *      remove a tag unhead is still tracking. That matters: unhead keeps its
 *      own map of the elements it renders into, and one removed behind its
 *      back leaves it writing into a detached node, where the tag is gone from
 *      the page for good.
 *
 * WHAT HAPPENS TO THE TAGS NOBODY CLAIMS: nothing, because there are none.
 * src/scripts/publicHead.js emits every tag the injector does, so on a public
 * page the first render claims the whole static head and from then on the view
 * owns it -- including across client-side navigation, where a leftover
 * og:image:alt or og:image:width would otherwise still be describing the page
 * the reader arrived on. tests/unit/publicViewHeads.test.js is what keeps that
 * completeness true per view; the sweep does not need a second mechanism for it.
 *
 * THE DEV SERVER HAS NOTHING TO DO HERE. It serves the raw template, which
 * carries no injected tags, so every sweep below finds nothing. There is no
 * dev-only branch to maintain, and the same is true inside the Capacitor
 * shells, which load their own un-injected copy of app.html.
 */
import { MARKER_ATTR, MARKER_VALUE, STATIC_HEAD_SELECTOR } from "@/data/headMarker.js";

/**
 * What makes two head tags the same tag.
 *
 * Deliberately the same identity @unhead/shared's tagDedupeKey computes, so
 * "this module would sweep it" and "unhead would have replaced it" describe the
 * same set. `property` and `name` stay DISTINCT keys, and that is a safety
 * decision rather than mere fidelity: `name="og:x"` is not Open Graph and no
 * preview crawler reads it, so collapsing the two would let a view still on the
 * wrong attribute delete the one valid tag on the page.
 *
 * @returns {string|null} null for tags that carry no identity (a preconnect, a
 *   script, a style) and therefore can never be anybody's duplicate.
 */
export function headTagKey(element) {
  const tag = element.tagName.toLowerCase();
  if (tag === "link" && element.getAttribute("rel") === "canonical") return "canonical";
  const id = element.getAttribute("id");
  if (id) return `${tag}:id:${id}`;
  if (tag === "meta") {
    for (const attribute of ["name", "property", "http-equiv"]) {
      const value = element.getAttribute(attribute);
      if (value !== null) return `meta:${attribute}:${value}`;
    }
  }
  return null;
}

/**
 * Mark an element as no longer purely static, because unhead now writes it.
 *
 * Called for every tag unhead renders. It keeps the element -- deleting it
 * would remove a live tag and leave unhead updating a detached node.
 */
export function claimStaticTag(element) {
  // unhead fires its renderTag hook for the document title too, and that
  // context carries no element.
  element?.removeAttribute?.(MARKER_ATTR);
}

/**
 * Remove static tags that a live tag has duplicated.
 *
 * @returns {number} how many were removed, so a caller can assert on it.
 */
export function dedupeStaticHeadTags(doc) {
  const head = doc?.head;
  if (!head) return 0;

  const marked = [];
  const liveKeys = new Set();
  for (const element of head.children) {
    const key = headTagKey(element);
    if (key === null) continue;
    if (element.getAttribute(MARKER_ATTR) === MARKER_VALUE) marked.push({ key, element });
    // Anything unmarked is somebody else's tag. Two unmarked descriptions are
    // a bug this sweep has no business silently editing.
    else liveKeys.add(key);
  }

  let removed = 0;
  for (const { key, element } of marked) {
    if (!liveKeys.has(key)) continue;
    element.remove();
    removed += 1;
  }
  return removed;
}

/** Head tags the build wrote that the client has not claimed yet. */
export function unclaimedStaticTags(doc) {
  return [...(doc?.head?.querySelectorAll(STATIC_HEAD_SELECTOR) || [])];
}

/**
 * Wire the two rules above onto the real head. Called once, at boot, before
 * anything mounts, because the first render is the one that matters.
 */
export function initStaticHeadDedupe({ head, document: injectedDocument }) {
  // Injectable so the tests can drive the real wiring against a document they
  // built, rather than mutating the one the test runner shares.
  const doc = injectedDocument || document;
  head.hooks.hook("dom:renderTag", (ctx) => claimStaticTag(ctx?.$el));
  head.hooks.hook("dom:rendered", () => dedupeStaticHeadTags(doc));
}
